import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RulePicker } from './RulePicker.js';

const OPERATORS = [
  'EQUAL_TO', 'NOT_EQUAL_TO', 'GREATER_THAN', 'LESS_THAN', 'GREATER_THAN_OR_EQUAL',
  'LESS_THAN_OR_EQUAL', 'IN', 'NOT_IN', 'BETWEEN', 'CONTAINS', 'NOT_CONTAINS',
  'IS_NULL', 'IS_NOT_NULL',
];
const VALUE_TYPES = ['NUMBER', 'STRING', 'BOOLEAN', 'DATE', 'LIST'];
const LOGICAL_OPERATORS = ['AND', 'OR', 'NOT'];

export function M24ConditionBuilderPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [ruleId, setRuleId] = useState('');
  const [ruleLabel, setRuleLabel] = useState('');
  const [factField, setFactField] = useState('');
  const [operator, setOperator] = useState('GREATER_THAN');
  const [valueText, setValueText] = useState('');
  const [valueType, setValueType] = useState('NUMBER');
  const [logicalOperator, setLogicalOperator] = useState('AND');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    apiRequest('/v1/rule-conditions', {
      method: 'POST',
      token,
      body: {
        ruleId,
        factField,
        operator,
        valueText: valueText || undefined,
        valueType,
        logicalOperator,
      },
    })
      .then(() => { setMessage(tr('m24.conditionBuilder.msgAdded', { rule: ruleLabel })); setFactField(''); setValueText(''); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setSubmitting(false));
  }

  return (
    <main aria-label="condition-builder">
      <h1>{tr('m24.conditionBuilder.title')}</h1>
      <form aria-label="condition-form" onSubmit={onSubmit}>
        <RulePicker label={tr('m24.conditionBuilder.ruleId')} value={ruleId} onPick={(id, label) => { setRuleId(id); setRuleLabel(label); }} />
        <label htmlFor="factField">{tr('m24.conditionBuilder.factField')}</label>
        <input id="factField" value={factField} onChange={(e) => setFactField(e.target.value)} />
        <label htmlFor="operator">{tr('m24.conditionBuilder.operator')}</label>
        <select id="operator" value={operator} onChange={(e) => setOperator(e.target.value)}>
          {OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <label htmlFor="valueText">{tr('m24.conditionBuilder.value')}</label>
        <input id="valueText" value={valueText} onChange={(e) => setValueText(e.target.value)} />
        <label htmlFor="valueType">{tr('m24.conditionBuilder.valueType')}</label>
        <select id="valueType" value={valueType} onChange={(e) => setValueType(e.target.value)}>
          {VALUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label htmlFor="logicalOperator">{tr('m24.conditionBuilder.logicalOperator')}</label>
        <select id="logicalOperator" value={logicalOperator} onChange={(e) => setLogicalOperator(e.target.value)}>
          {LOGICAL_OPERATORS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <button type="submit" disabled={submitting || !ruleId.trim() || !factField.trim() || !operator.trim()}>{tr('m24.conditionBuilder.submit')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </main>
  );
}
